import "../../../assets/scss/billings-style.scss";

import { useEffect, useState } from "react";
import { ConfigProvider, Skeleton, Table, TableProps, Tag } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  getDepositBalanceHistory,
  getBillingsHistory,
} from "../../server/User";
import { DepositBalanceBtn } from "../DepositBalanceBtn";
import { useAppSelector } from "../../store/hooks";
import { filter } from "lodash";

interface DepositHistoryDataType {
  user_id?: number;
  amount?: string;
  operation?: string;
  currency?: string;
  payment_method?: string;
  payment_id?: string;
  status?: string;
  created_at?: number;
}

interface BillingsType {
  user_id?: number;
  amount?: string;
  transaction_id?: number;
  service_name?: string;
  instance_id?: number;
  status?: string;
  created_at?: number;
}

export const CreditDisplayTables = () => {
  const { t } = useTranslation();

  const [isDespositTable, setIsDespositTable] = useState<boolean>(true); // 判斷顯示儲值/扣點 table
  const [depositHistoryData, setDepositHistoryData] = useState<any[]>([]);
  const [billings, setBillingsData] = useState<BillingsType[]>([]);
  const [startTime, setStartTime] = useState<string>(""); // TODO: 之後 filter 可用
  const [endTime, setEndTime] = useState<string>(""); // TODO: 之後 filter 可用
  const [loading, setLoading] = useState<boolean>(false);
  const credit = useAppSelector((state: any) => state.user.credit);

  const depositHistoryColumns: TableProps<DepositHistoryDataType>["columns"] = [
    {
      title: t("Payment History"),
      dataIndex: "payment_history",
      key: "payment_history",
      render: (value, record) => (
        <>{moment.unix(record.created_at!).format("YYYY-MM-DD")}</>
      ),
    },
    {
      title: t("Deposit Id"),
      dataIndex: "payment_id",
      key: "payment_id",
      render: (value) => <>{value}</>,
    },
    {
      title: t("Credits"),
      dataIndex: "amount",
      key: "amount",
      render: (value) => (
        <span>
          +{value} {t("points")}
        </span>
      ),
    },
    {
      title: t("Paid Amount"),
      dataIndex: "currency",
      key: "currency",
      render: (value, record) => (
        <>
          {value === "USD"
            ? `USD$${Number(record.amount).toFixed(2)}`
            : `${value}$${(Number(record.amount) * 30).toFixed(2)}`}
        </>
      ),
    },
  ];

  const deductHistoryColumns: TableProps<BillingsType>["columns"] = [
    {
      title: t("Usage History"),
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => <>{moment.unix(value).format("YYYY-MM-DD")}</>,
    },
    {
      title: t("Transaction Id"),
      dataIndex: "transaction_id",
      key: "transaction_id",
    },
    {
      title: t("Credit Used"),
      dataIndex: "amount",
      key: "amount",
      render: (value) => (
        <span>
          {value} {t("points")}
        </span>
      ),
    },
    {
      title: t("Instance Id"),
      dataIndex: "instance_id",
      key: "instance_id",
    },
    {
      title: t("Service Name"),
      dataIndex: "service_name",
      key: "service_name",
    },
  ];

  const treatTemplateName = (templateName: string) => {
    const baseName = templateName.replace(/-\d+$/, "");
    return baseName;
  };

  const customizeRenderEmpty = () => (
    <div style={{ textAlign: "center" }}>
      <p>{t("No Data Yet")}</p>
    </div>
  );

  const getDepositHistoryData = async () => {
    setLoading(true);
    try {
      const response = await getDepositBalanceHistory(startTime, endTime);
      const data = await response.data.data;
      setDepositHistoryData(data.deposit_history || []);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const getDeductedBalanceData = async () => {
    setLoading(true);
    try {
      const response = await getBillingsHistory(1, 20);
      const data = await response.data;
      setBillingsData(data.billings);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getDepositHistoryData();
    getDeductedBalanceData();
  }, [credit]);

  return (
    <div className="billings-table-wrapper">
      <div className="table-title">
        <span onClick={() => setIsDespositTable(!isDespositTable)}>
          <span
            className={
              isDespositTable
                ? "billings-title-active"
                : "billings-title-inactive"
            }
          >
            {t("Payments")}
          </span>
          <span
            className={
              isDespositTable
                ? "billings-title-inactive"
                : "billings-title-active"
            }
          >
            {t("Usage")}
          </span>
        </span>
      </div>
      <div className="credit-table-wrapper">
        {loading ? (
          <Skeleton active paragraph />
        ) : depositHistoryData.length === 0 ? (
          <>{t("No Data Yet")}</>
        ) : (
          <ConfigProvider
            renderEmpty={customizeRenderEmpty}
            theme={{
              components: {
                Table: {
                  headerBg: "#F6F8FD",
                },
              },
            }}
          >
            {isDespositTable ? (
              <Table
                columns={depositHistoryColumns}
                dataSource={depositHistoryData}
                showHeader={depositHistoryData.length !== 0}
                tableLayout="fixed"
                pagination={{
                  position: ["bottomCenter"],
                  showSizeChanger: false,
                }}
                scroll={{ x: "max-content" }}
                loading={loading}
              />
            ) : (
              <Table
                columns={deductHistoryColumns}
                dataSource={billings}
                showHeader={billings.length !== 0}
                tableLayout="fixed"
                pagination={{
                  position: ["bottomCenter"],
                  showSizeChanger: false,
                }}
                scroll={{ x: "max-content" }}
                loading={loading}
              />
            )}
          </ConfigProvider>
        )}
      </div>
    </div>
  );
};
